import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from "framer-motion";

// --- FUTURISTIC BACKGROUND ---
function FuturisticBackground() {
  const { scrollY } = useScroll();
  const yGrid = useTransform(scrollY, [0, 1000], [0, -150]);
  const rotateGrid = useTransform(scrollY, [0, 1000], [0, 5]);
  const yOrb2 = useTransform(scrollY, [0, 1000], [0, -200]);
  const rotateRing = useTransform(scrollY, [0, 2000], [0, 360]);
  const smoothRotate = useSpring(rotateRing, { stiffness: 50, damping: 20 });

  return (
    <div className="fixed inset-0 overflow-hidden -z-10 bg-[#003049]">
      <div className="absolute inset-0 bg-gradient-to-br from-[#001824] via-[#003049] to-[#004e75]" />
      <motion.div
        style={{ y: yGrid, rotateX: rotateGrid }}
        className="absolute inset-[-50%] w-[200%] h-[200%] origin-top 
        bg-[linear-gradient(to_right,#457B9D22_1px,transparent_1px),
        linear-gradient(to_bottom,#457B9D22_1px,transparent_1px)]
        bg-[size:4rem_4rem] 
        [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)]
        perspective-1000"
      />
      <motion.div
        style={{ y: yOrb2 }}
        className="absolute bottom-[10%] right-[5%] w-[40vw] h-[40vw] 
        bg-[#457B9D] rounded-full blur-[140px] opacity-30 mix-blend-screen"
      />
      <motion.div
        style={{ rotate: smoothRotate }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
        w-[800px] h-[800px] border border-[#D1D1D1]/10 rounded-full border-dashed pointer-events-none"
      />
    </div>
  );
}

// --- IMAGE LOADING LOGIC ---
let galleryImages = [];
try {
    const importAll = (r) => r.keys().map(key => {
        const fileName = key.replace(/^.*[\\\/]/, '');
        return {
            src: r(key).default || r(key),
            id: key,
            title: fileName.split('.')[0].replace(/[-_]/g, ' '), // Generate title from filename
            category: 'Gallery'
        };
    });
    // Load all requested formats
    galleryImages = importAll(require.context('../assets/images/gallery', false, /\.(png|jpe?g|svg|webp|avif|bmp|gif)$/i));
} catch (error) {
    console.warn("Gallery folder not found or empty. Using fallback images.", error);
}

const GalleryPage = () => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [spans, setSpans] = useState({});

    const handleImageLoad = (id, { width, height }) => {
        const ratio = width / height;
        let span = {};
        if (ratio > 1.5) span = { gridColumn: 'span 2' };
        else if (ratio < 0.6) span = { gridRow: 'span 2' };
        
        setSpans(prev => ({ ...prev, [id]: span }));
    };

    return (
        <div className="font-sans min-h-screen pt-28 px-4 pb-12 text-gray-200 relative overflow-hidden selection:bg-[#457B9D] selection:text-white">
            <FuturisticBackground />
            
            {/* Custom CSS for the Mosaic Grid Layout */}
            <style>{`
                .custom-gallery {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(12.5rem, 1fr));
                    grid-auto-rows: 12.5rem;
                    grid-auto-flow: dense;
                    gap: 0.75rem;
                }
            `}</style>

            <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="relative z-10 max-w-6xl mx-auto"
            >
                <div className="text-center mb-12">
                    <h1 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tighter uppercase drop-shadow-[0_0_15px_rgba(69,123,157,0.5)]">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#457B9D] to-[#D1D1D1]">Gallery</span>
                    </h1>
                    <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                        Explore our futuristic vision through our curated collection.
                    </p>
                </div>

                <motion.div 
                    layout
                    className="custom-gallery max-w-7xl mx-auto"
                >
                    <AnimatePresence>
                    {galleryImages.map((item, index) => (
                        <motion.figure 
                            layout
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.4 }}
                            key={item.id || index} 
                            style={spans[item.id || index]}
                            className="group relative overflow-hidden cursor-pointer bg-[#001824] rounded-xl border border-[#457B9D]/30 hover:border-[#D1D1D1]/50 transition-colors"
                            onClick={() => setSelectedImage(item)}
                        >
                            {/* Image */}
                            <img 
                                src={item.src} 
                                alt={item.title} 
                                className="w-full h-full object-cover transition-transform duration-1000 ease-in-out group-hover:scale-110"
                                onLoad={(e) => {
                                    handleImageLoad(item.id || index, { 
                                        width: e.target.naturalWidth, 
                                        height: e.target.naturalHeight 
                                    });
                                }}
                            />
                            
                            {/* Figcaption Overlay */}
                            {/* <figcaption className="absolute inset-0 flex flex-col justify-end p-6 bg-gradient-to-t from-[#001824]/90 via-[#001824]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <h3 className="text-xl font-bold text-white mb-1 translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                                    {item.title}
                                </h3>
                                <p className="text-[#457B9D] text-sm font-mono uppercase tracking-widest translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-75">
                                    {item.category}
                                </p>
                            </figcaption> */}
                        </motion.figure>
                    ))}
                    </AnimatePresence>
                </motion.div>
            </motion.div>

            {selectedImage && (
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
                    onClick={() => setSelectedImage(null)}
                >
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="relative max-w-5xl w-full max-h-[90vh] bg-[#001824] border border-[#457B9D] shadow-[0_0_50px_rgba(69,123,157,0.2)] flex flex-col"
                        style={{ clipPath: "polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)" }}
                        onClick={e => e.stopPropagation()}
                    >
                        <button 
                            onClick={() => setSelectedImage(null)}
                            className="absolute top-0 right-0 z-10 bg-[#457B9D] hover:bg-white hover:text-[#001824] text-white p-3 transition-colors rounded-bl-xl"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        
                        <div className="flex-1 overflow-auto flex items-center justify-center bg-[radial-gradient(circle_at_center,#003049_0%,#001824_100%)]">
                            <img 
                                src={selectedImage.src} 
                                alt={selectedImage.title} 
                                className="max-w-full max-h-[80vh] object-contain"
                            />
                        </div>
                        
                        {/* <div className="p-6 bg-[#001824] border-t border-[#457B9D]/30 flex justify-between items-end">
                            <div>
                                <h2 className="text-3xl font-bold text-white mb-1 tracking-tight">{selectedImage.title}</h2>
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 bg-[#457B9D] rounded-full animate-pulse"/>
                                    <p className="text-[#457B9D] font-mono text-sm uppercase tracking-widest">{selectedImage.category}</p>
                                </div>
                            </div>
                            <div className="hidden md:block text-right">
                                <p className="text-gray-500 font-mono text-xs">IMG_ID_{selectedImage.id.replace(/\D/g, '').slice(-4) || '0001'}</p>
                                <p className="text-gray-500 font-mono text-xs">SECURE_VIEW</p>
                            </div>
                        </div> */}
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default GalleryPage;